import { useState, type ReactNode } from 'react';
import { stripeApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui';
import { RenonciationRetractation } from '@/components/RenonciationRetractation';
import { cn } from '@/lib/utils';
import { OFFRE_COMPLETE, prixParPeriode } from '@/lib/tarifs';

type Rythme = 'monthly' | 'annual';

const RYTHMES: { valeur: Rythme; titre: string; prix: string; detail: string }[] = [
    {
        valeur: 'monthly',
        titre: 'Mensuel',
        prix: prixParPeriode(OFFRE_COMPLETE.mensuel, 'mois'),
        detail: 'Sans engagement',
    },
    {
        valeur: 'annual',
        titre: 'Annuel',
        prix: prixParPeriode(OFFRE_COMPLETE.annuelParMois, 'mois'),
        detail: `soit ${prixParPeriode(OFFRE_COMPLETE.annuel, 'an')}`,
    },
];

interface PassageOffreCompleteProps {
    onAnnuler?: () => void;
}

/**
 * Formule Retenue -> offre complète : l'artisan choisit mensuel ou annuel, puis le
 * serveur change la formule (même compte, même abonnement). Le profil est rechargé :
 * toutes les rubriques s'ouvrent. Un échec le dit, et rien ne change.
 */
export function PassageOffreComplete({ onAnnuler }: PassageOffreCompleteProps): ReactNode {
    const { user, refreshUser } = useAuth();
    const toast = useToast();
    const [rythme, setRythme] = useState<Rythme | null>(null);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState('');
    const [renonciation, setRenonciation] = useState(false);
    // Avec un abonnement en cours, le passage change un abonnement payant : le serveur
    // exige la renonciation au droit de rétractation. L'appli ne voit pas l'abonnement
    // Stripe, et un artisan qui a payé pendant son essai en a un tout en restant en
    // TRIAL : la case est demandée à tous, le serveur l'ignore quand rien n'est facturé.
    const renonciationRequise = true;

    // Le serveur réserve ce changement au titulaire du compte.
    if (user && user.role !== 'OWNER') {
        return (
            <p className="text-sm text-[#475569] dark:text-[#94A3B8]">
                Seul le titulaire du compte peut passer à l'offre complète.
            </p>
        );
    }

    const confirmer = async () => {
        if (!rythme) return;
        setEnCours(true);
        setErreur('');
        try {
            await stripeApi.changerFormule(rythme, renonciation);
        } catch (err: unknown) {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? 'Réessayez dans un instant ou écrivez-nous à contact@btpal.fr.';
            setErreur(`Le passage à l'offre complète n'a pas abouti, votre formule n'a pas changé. ${message}`);
            setEnCours(false);
            return;
        }
        await refreshUser().catch(() => undefined);
        // Un compte bloqué (essai fini, rien de payé) reste bloqué après un passage
        // gratuit : lui dire que tout est ouvert serait faux.
        if (user?.subscriptionStatus === 'BLOCKED') {
            toast.info("Vous êtes passé à l'offre complète.", "Votre compte reste bloqué tant que l'abonnement n'est pas payé.");
        } else {
            toast.success("Vous êtes passé à l'offre complète.", 'Toutes les rubriques sont maintenant ouvertes.');
        }
        setEnCours(false);
    };

    return (
        <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-[#1E293B] dark:text-[#F1F5F9] mb-2">
                Choisissez votre rythme de paiement
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {RYTHMES.map((option) => (
                    <label
                        key={option.valeur}
                        className={cn(
                            'rounded-lg border p-3 cursor-pointer transition-colors has-focus-visible:ring-2 has-focus-visible:ring-[#0F2744]/30',
                            rythme === option.valeur
                                ? 'border-[#0F2744] bg-[#0F2744]/5 dark:border-[#3B82F6] dark:bg-[#3B82F6]/10'
                                : 'border-[#E2E8F0] hover:border-[#94A3B8] dark:border-[#334155] dark:hover:border-[#475569]'
                        )}
                    >
                        <input
                            type="radio"
                            name="rythme-offre-complete"
                            value={option.valeur}
                            checked={rythme === option.valeur}
                            onChange={() => setRythme(option.valeur)}
                            className="sr-only"
                        />
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-[#64748B] dark:text-[#94A3B8]">{option.titre}</span>
                        <span className="block text-lg font-bold text-[#1E293B] dark:text-[#F1F5F9]">{option.prix}</span>
                        <span className="block text-xs text-[#64748B] dark:text-[#94A3B8]">{option.detail}</span>
                    </label>
                ))}
            </div>
            <p className="text-xs text-[#64748B] dark:text-[#94A3B8]">
                Si vous avez déjà payé votre année, la différence est prélevée tout de suite sur votre moyen de paiement. Si ce paiement est refusé, rien ne change.
            </p>
            {renonciationRequise && (
                <RenonciationRetractation
                    acceptee={renonciation}
                    onChange={(acceptee) => {
                        setRenonciation(acceptee);
                        if (acceptee) setErreur('');
                    }}
                />
            )}
            {erreur && (
                <div role="alert" className="p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#991B1B] dark:bg-[#7F1D1D]/30 dark:border-[#7F1D1D] dark:text-[#FECACA]">
                    {erreur}
                </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button onClick={confirmer} disabled={!rythme || (renonciationRequise && !renonciation)} isLoading={enCours}>
                    Confirmer le passage à l'offre complète
                </Button>
                {onAnnuler && (
                    <Button variant="outline" onClick={onAnnuler} disabled={enCours}>
                        Annuler
                    </Button>
                )}
            </div>
        </fieldset>
    );
}
